import * as React from 'react';

import SelectField, {OptionValue} from "../components/SelectField";
import deepEqual from 'deep-equal';
import {EActiveMarketKey, PropertiesKey} from '../views/OptionsView/OptionsViewUtils';

/** interface for object, containing the properties according to keys of adapters */
export interface IDependentSelectFilter {
    /** properties (named as keys) of all adapter */
    [key: string]: OptionValue | undefined;
}

/** data adapter interface for single selector */
export interface IDependentSelectDataAdapter {
    /**
     * function which returns a custom way to render the value to string
     * @default option.toString()
     */
    renderer?: (option: OptionValue, index?: number) => string;
    /** @return key, by which the selected value will be added to filter */
    getKey(): string;
    /** @return array of objects, string, numbers or booleans */
    getData(): Array<OptionValue>;

    /** @return current filter, it can be updated by data adapter */
    getFilter(): IDependentSelectFilter;
    /**
     * sets the new filter with the properties (named as keys) of all
     * adapter.
     *
     * @param {IDependentSelectFilter} filter
     * @returns {Promise<void>}
     * @memberof IDependentSelectDataAdapter
     */
    setFilter(filter: IDependentSelectFilter): Promise<void>;
}

export enum EDirection {
    Horizontal = 0,
    Vertical
}

interface IDependentSelectProps {
    /** optional style to apply to the control */
    style?: React.CSSProperties;
    /** array of DataAdapters */
    dataAdapters: Array<IDependentSelectDataAdapter>;

    /** array of placeholder, containing the texts shown for the empty SelectFields */
    placeholders?: Array<string>;

    /** array of readonly, containing the readonly flag for SelectFields */
    readonlies?: Array<boolean>;

    /** initial values */
    value?: IDependentSelectFilter;

    /** title to be displayed */
    title?: string;

    /** properties to be applied to title */
    titleStyle?: React.CSSProperties;

    /**
     * direction of elements
     * @default EDirection.Horizontal
     */
    direction?: EDirection;

    /** on change handler */
    onChange?(value: IDependentSelectFilter): void;

    boldFeature?: boolean;

    /** Flag for the cnr dropdown -> basic market/ specific markets */
    activeMarket?: EActiveMarketKey;

    /**
     * Flag for whether or not the cnr dropdown should be displayed
     * Used currently only for the copy PSL tab
     * It's still necesarry to have a cnr selected in order cu highlight the PSLs that have data
     */
    invisibleCnr?: boolean;
}

interface IDepSelItem {
    /** current data after filtering */
    dataSource: Array<OptionValue>;
    /** max chars length of single item in dataSource */
    maxChars: number;
}

interface IDependentSelectState {
    /** sum of all maxChars of Items */
    maxChars: number;
    /** current filter */
    filter: IDependentSelectFilter;
    /** dataSources */
    [dataSource: number]: IDepSelItem;
}

/** displays the list of select fields for data adapters */
class DependentSelect extends React.Component<IDependentSelectProps, IDependentSelectState>
{
    /** updates the data sources */
    private static getData(dataSources: Array<IDependentSelectDataAdapter>): {maxChars: number, [dataSource: number]: IDepSelItem, filter: IDependentSelectFilter} {
        let result: {maxChars: number, [dataSource: number]: IDepSelItem, filter: IDependentSelectFilter} = {maxChars: 0, filter: {}};
        dataSources.forEach((adapter: IDependentSelectDataAdapter, index: number) => {
            let data = adapter.getData();
            Object.assign(result.filter, adapter.getFilter());
            let maxChars = 0;
            data.forEach((value: OptionValue) => {
                const sval = adapter.renderer ? adapter.renderer(value) : value.toString();
                maxChars = Math.max(maxChars, sval.length);
            }
            );
            result.maxChars += maxChars;
            result[index] = {dataSource: data, maxChars};
        });
        return result;
    }

    constructor(props: IDependentSelectProps) {
        super(props);

        let state = {maxChars: 0, filter: {}};
        Object.assign(state, DependentSelect.getData(props.dataAdapters), {filter: {...props.value}});
        this.state = state;

        // This binding is necessary to make `this` work in the callback
        // this.onSelectionChanged = this.onSelectionChanged.bind(this);
    }

    callOnChange() {
        if (this.props.onChange)
            this.props.onChange(this.state.filter);
    }

    componentWillReceiveProps(newProps: IDependentSelectProps) {
        // update only changed properties:
        let newState: IDependentSelectState = {...this.state}; // not deep clone object
        const adpChanged = newProps.dataAdapters !== this.props.dataAdapters;
        const valChanged = newProps.value !== this.props.value;
        if (adpChanged || valChanged) {
            Object.assign(newState, DependentSelect.getData(newProps.dataAdapters));
        }

        newState.filter = valChanged ? {...newProps.value} : newState.filter;

        const isCallOnChange = !deepEqual(newState.filter, newProps.value) || !deepEqual(newState.filter, this.props.value);

        if (adpChanged || valChanged) {
            this.setState(newState, () => {
                if (isCallOnChange)
                    this.callOnChange();
            });
        }
    }

    componentDidUpdate(prevProps: IDependentSelectProps, prevState: IDependentSelectState) {
        const { dataAdapters, activeMarket } = this.props;

        // Ensure basic market selection is handled outside of render
        if (activeMarket === EActiveMarketKey.BasicMarketActiveKey) {
            dataAdapters.forEach((adapter, index) => {
                const key = adapter.getKey();
                if (
                    (key === PropertiesKey.CnrKey || key === PropertiesKey.SourceCnrKey || key === PropertiesKey.TargetCnrKey) &&
                    this.state.filter[key] === undefined &&
                    this.state[index] && this.state[index].dataSource && this.state[index].dataSource.length > 0 // Updated condition
                ) {
                    this.onSelectionChanged(index, this.state[index].dataSource[0]);
                }
            });
        }
    }

    onSelectionChanged(index: number, selectedValue?: OptionValue) {
        let newState: any = {};

        // copy only the properties which keys are contained in adapters
        const {filter} = this.state;
        let newFilter: IDependentSelectFilter = {};
        this.props.dataAdapters.forEach((adapter, ind) => {
            const k = adapter.getKey();
            // set new selected value
            // clear values for all select values that follow the current index(order dependency on select)
            // keep values for the select fields above the current index
            newFilter[k] = ind === index ? selectedValue : ind > index ? undefined : filter[k];
        });
        newState.filter = newFilter;

        Promise.all(this.props.dataAdapters.map((adp: IDependentSelectDataAdapter) => adp.setFilter(newFilter)))
            .then(() => {
                // update all data sources after changing of filter
                Object.assign(newState, DependentSelect.getData(this.props.dataAdapters));
                this.setState(newState, () => this.callOnChange());
            }
            )
            .catch((reason: any) => console.error(reason));
    }

    render() {
        const styleSF =
        {
            width: '*',
        };

        const { direction = EDirection.Horizontal, dataAdapters, placeholders, readonlies, title } = this.props;

        const lim = dataAdapters.length - 1;
        const selectors = dataAdapters
            .map((value: IDependentSelectDataAdapter, index: number) => {
                // set proportional width
                let st = {...styleSF};
                // TODO: calc the cw by font width and clear x and drop V
                const cw = 9; // width in chars of clear x and drop V
                const wa = this.state.maxChars + dataAdapters.length * cw;

                if (index < lim)
                    st.width = Math.floor((this.state[index].maxChars + cw) / wa * 100) + '%';
                const adapter = dataAdapters[index];
                const placeholder = placeholders && placeholders.length > index ? placeholders[index] : undefined;
                const readonly = readonlies && readonlies.length > index ? readonlies[index] : undefined;

                const cont =
                    this.props.invisibleCnr &&
                    (adapter.getKey() === PropertiesKey.SourceCnrKey || adapter.getKey() === PropertiesKey.TargetCnrKey) ?
                    (<></>)
                    :
                    (
                        <td key={index} style={st}>
                            <SelectField
                                key={adapter.getKey()}
                                placeholder={placeholder}
                                readonly={readonly}
                                dataSource={this.state[index].dataSource}
                                value={this.state.filter[adapter.getKey()]}
                                disabled={false}
                                rendererKey={adapter.renderer}
                                onChange={(selectedValue?: OptionValue) =>
                                    this.onSelectionChanged(index, selectedValue)}
                                boldFeaturePSLAssigned={this.props.boldFeature}
                            />
                        </td>
                    );
                if (direction !== EDirection.Vertical) {
                    return cont;
                }
                else {
                    return (
                        <tr key={"row" + index}>
                            {cont}
                        </tr>

                    );
                }
            }
            );
        const style =
        {
            width: '100%'
        };
        Object.assign(style, this.props.style); // adopt external style

        let headerStyle: React.CSSProperties = {textAlign: 'left', fontWeight: 'normal'};
        Object.assign(headerStyle, this.props.titleStyle);

        const header = !title ? null :
            (
                <thead>
                    <tr>
                        <th colSpan={direction !== EDirection.Vertical ? dataAdapters.length : 1} style={headerStyle}>{title + ':'}</th>
                    </tr>
                </thead>
            );

        const body = direction !== EDirection.Vertical ? (
            <tr>
                {selectors}
            </tr>
        ) : selectors;

        return (
            <table style={style}>
                {header}
                <tbody>
                    {body}
                </tbody>
            </table>
        );
    }
}
export default DependentSelect;



// WEBPACK FOOTER //
// ./src/components/DependentSelect.tsx